import { useEffect, useRef, useCallback } from 'react'
import {
  MapFileTypes,
  COLLAB_SUPPORTED_GOOGLE_FILE_TYPES,
  GOOGLE_DRIVE_APP_NAME,
} from '../../constants'
import {
  useAppDispatch,
  useSelectRunningContext,
  useSetDialog,
  useSelectMobileDeviceInfo,
} from '../../hooks'
import { useSelectAuth, useSelectOrigin } from '../../hooks'

import { setPage, setIsGeneralSpinner } from '../../app/root'
import { setFile } from '../../app/file'
import { setDialog } from '../../app/dialog'
import { useTrackMetricMutation } from '../../app/appApiQueries'
import './GoogleFilePicker.css'

// The google picker is meant to be a dialog modal, and we manually manipulate styles to force it to look like a regular component. It is appended to the html structure outside of the root
const GoogleFilePicker = () => {
  const dispatch = useAppDispatch()
  const dialogTrigger = useSetDialog('Google')
  const auth = useSelectAuth('Google')
  const googleAccessToken = auth?.accessToken
  const hasAccessToken = Boolean(
    googleAccessToken?.length && typeof googleAccessToken === 'string'
  )
  const pickerRef = useRef(null)
  const runningContext = useSelectRunningContext()
  const origin = useSelectOrigin()
  const { isMobileDevice, isLatestMobileClient } = useSelectMobileDeviceInfo()
  const [trackMetric] = useTrackMetricMutation()
  const pickerCallback = useCallback(
    (data) => {
      try {
        if (data.action === 'loaded') {
          // not in documentation, found while testing
          // Track successful file selection
          trackMetric({
            measure: 'file_picker_open_success',
            tag: { appName: GOOGLE_DRIVE_APP_NAME },
            fields: { success: true },
          }).catch(() => {
            // Swallow metric tracking errors
          })
        }
        if (data.action === window.google.picker.Action.CANCEL) {
          if (isMobileDevice) {
            //Use closeApp() API on iOS and Android Clients 6.5.5 & Above
            if (isLatestMobileClient) {
              zoomSdk.closeApp().catch(() => {
                //swallow
              })
            } else {
              //Do Nothing, closeApp() API not supported on older clients.
            }
          } else {
            zoomSdk.sendAppToBackground().catch(() => {
              //swallow
            })
          }
        } else if (data.action === window.google.picker.Action.ERROR) {
          // Track picker error
          trackMetric({
            measure: 'file_picker_open_success',
            tag: { appName: GOOGLE_DRIVE_APP_NAME },
            fields: { success: false },
          }).catch(() => {
            // Swallow metric tracking errors
          })
          dispatch(setPage('error'))
        } else {
          if (data.action === window.google.picker.Action.PICKED) {
            const pickedFile = data?.docs[0]
            const type =
              MapFileTypes[pickedFile.mimeType] ?? pickedFile.mimeType
            dispatch(
              setFile({
                name: pickedFile.name,
                url: pickedFile.url,
                type,
                id: pickedFile.id,
              })
            )
            if (
              runningContext === 'inMainClient' ||
              runningContext === 'inChat' ||
              isMobileDevice
            ) {
              zoomSdk.openUrl({ url: pickedFile.url })
            } else if (
              runningContext === 'inCollaborate' &&
              origin !== 'Meeting Chat'
            ) {
              dispatch(setDialog({ type: 'collaborationNoShare' }))
            } else {
              dialogTrigger({ drive: 'Google', fileId: pickedFile.id })
            }
          }
        }
      } catch (e) {
        dispatch(setPage('error'))
      }
    },
    [
      isMobileDevice,
      isLatestMobileClient,
      dispatch,
      runningContext,
      origin,
      dialogTrigger,
      trackMetric,
    ]
  )

  const initializePicker = useCallback(() => {
    const allDocs = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setLabel('All files')
      .setMimeTypes(COLLAB_SUPPORTED_GOOGLE_FILE_TYPES.join())

    const sharedWithMe = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setOwnedByMe(false)
      .setMimeTypes(COLLAB_SUPPORTED_GOOGLE_FILE_TYPES.join())

    const myDrive = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setOwnedByMe(true)
      .setMimeTypes(COLLAB_SUPPORTED_GOOGLE_FILE_TYPES.join())

    const starredFiles = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setStarred(true)
      .setLabel('Starred')
      .setMimeTypes(COLLAB_SUPPORTED_GOOGLE_FILE_TYPES.join())

    const picker = new google.picker.PickerBuilder()
      .addView(myDrive)
      .addView(sharedWithMe)
      .addView(starredFiles)
      .addView(allDocs)
      .setOAuthToken(googleAccessToken)
      .setCallback(pickerCallback)
      .setSize('100%', '650') // picker modal is auto-centered by default. minimum size (566,350) and a maximum size (1051,650). // may need to change for zoom apps size
      .hideTitleBar()
      .build()
    pickerRef.current = picker
  }, [pickerCallback, googleAccessToken])

  // spinner on load
  useEffect(() => {
    dispatch(setIsGeneralSpinner(true))
  }, [dispatch])

  useEffect(() => {
    try {
      if (
        window?.google?.picker &&
        hasAccessToken &&
        !pickerRef?.current?.isVisible()
      ) {
        // must wait for google window variable to load for slower internet
        initializePicker()
        pickerRef?.current?.setVisible(true)
        dispatch(setIsGeneralSpinner(false))

        // Note: We only track final outcomes (file picked or error) in the callback
        // to avoid duplicate metrics
      } else {
        // may render another picker
        window.addEventListener('googleScriptLoaded', initializePicker)
      }
    } catch (e) {
      dispatch(setPage('error'))
    }
    return () => {
      // destroy the created picker so we do not create more than one on re-renders
      pickerRef?.current?.dispose()
    }
  }, [googleAccessToken, hasAccessToken, pickerRef, initializePicker, dispatch])

  // adjusts the height of the picker during collab
  useEffect(() => {
    const content = document.querySelector('.picker-dialog-content')
    if (runningContext === 'inCollaborate') {
      if (content) {
        content.classList.add('google-picker-collab-height')
      }
    } else {
      if (content) {
        content.classList.remove('google-picker-collab-height')
      }
    }
  }, [runningContext])

  // returns nothing and behaves more like a hook, but needs to be conditionally rendered in parent, so acts as a component
  return undefined
}

export default GoogleFilePicker
